import { Link, useNavigate } from 'react-router-dom';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { ScrollText, ChevronRight } from 'lucide-react';

export function ConditionsGeneralesUtilisationPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MarketingHeader
        onNavigate={(path) => navigate(path)}
        onSignIn={() => navigate('/login')}
        onSignUp={() => navigate('/login')}
      />

      <main className="flex-1">
        <div className="bg-gradient-to-b from-slate-50 to-white border-b border-gray-100 py-12 sm:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/" className="hover:text-blue-600 transition-colors">Accueil</Link>
              <ChevronRight size={14} />
              <span className="text-gray-800 font-medium">Conditions Générales d'Utilisation</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <ScrollText size={20} className="text-blue-600" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Conditions Générales d'Utilisation
              </h1>
            </div>
            <p className="text-gray-500 text-sm mt-2">
              Dernière mise à jour : 09/03/2026
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="space-y-10 text-gray-700 leading-relaxed">

            <p>
              Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation de la plateforme TrezoOne.
              TrezoOne est une solution logicielle accessible en ligne permettant aux associations, écoles et organisations de suivre et d'anticiper leur trésorerie.
              L'utilisation du service implique l'acceptation pleine et entière des présentes CGU.
            </p>

            <Section title="1. Éditeur du service">
              <p>Le service TrezoOne est édité par :</p>
              <div className="mt-4 p-5 bg-gray-50 rounded-xl border border-gray-200 text-sm space-y-2">
                <p className="font-semibold text-gray-900">One System – SASU</p>
                <p className="text-gray-600">Société par actions simplifiée unipersonnelle</p>
                <div className="pt-2 border-t border-gray-200 space-y-1.5">
                  <Row label="Siège social" value="1484 Route des Gabloteaux, 16130 Juillac le coq" />
                  <Row label="SIREN" value="101 790 491" />
                  <Row label="RCS" value="Angoulême" />
                </div>
                <div className="pt-2 border-t border-gray-200 space-y-1.5">
                  <Row label="Directeur de la publication" value="Maxime Gacher" />
                  <div className="flex flex-wrap gap-1">
                    <span className="text-gray-500 min-w-[140px]">Contact</span>
                    <a href="mailto:support@trezoone.fr" className="text-blue-600 hover:underline">
                      support@trezoone.fr
                    </a>
                  </div>
                </div>
              </div>
            </Section>

            <Section title="2. Description du service">
              <p>TrezoOne est un logiciel accessible en ligne permettant notamment :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>le suivi de trésorerie</li>
                <li>la gestion des prévisions financières</li>
                <li>l'analyse de la situation financière d'une organisation</li>
                <li>la visualisation d'indicateurs financiers</li>
              </ul>
              <p className="mt-4">TrezoOne constitue un outil d'aide à la décision.</p>
              <p className="mt-3">TrezoOne :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>ne constitue pas un logiciel comptable certifié</li>
                <li>ne constitue pas un service de conseil financier</li>
                <li>ne constitue pas un service de conseil juridique</li>
                <li>ne constitue pas un service d'expertise comptable</li>
              </ul>
              <p className="mt-4">TrezoOne ne remplace pas un expert-comptable.</p>
            </Section>

            <Section title="3. Accès au service">
              <p>L'accès au service nécessite la création d'un compte utilisateur.</p>
              <p className="mt-3">L'utilisateur s'engage à fournir des informations exactes lors de l'inscription.</p>
              <p className="mt-3">L'utilisateur est responsable de la confidentialité de ses identifiants.</p>
              <p className="mt-3">Toute utilisation du compte est réputée effectuée par son titulaire.</p>
              <p className="mt-3">L'utilisateur s'engage à informer immédiatement l'éditeur en cas d'utilisation frauduleuse de son compte.</p>
            </Section>

            <Section title="4. Utilisation du service">
              <p>L'utilisateur s'engage à :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>utiliser TrezoOne conformément à sa destination</li>
                <li>fournir des informations exactes et à jour</li>
                <li>ne pas utiliser le service à des fins frauduleuses ou illégales</li>
                <li>ne pas tenter d'accéder de manière non autorisée aux systèmes du service</li>
                <li>ne pas perturber le fonctionnement de la plateforme</li>
              </ul>
              <p className="mt-4">Toute utilisation abusive peut entraîner la suspension ou la suppression du compte.</p>
            </Section>

            <Section title="5. Données saisies par les utilisateurs">
              <p>Les données saisies dans TrezoOne restent la propriété exclusive des utilisateurs ou de leur organisation.</p>
              <p className="mt-3">L'éditeur n'acquiert aucun droit sur ces données, sauf dans la mesure nécessaire au fonctionnement du service.</p>
              <p className="mt-3">L'utilisateur est seul responsable :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>de l'exactitude des données saisies</li>
                <li>de la mise à jour des informations</li>
                <li>de l'interprétation des analyses générées par le logiciel</li>
                <li>des décisions prises sur la base des informations fournies par la plateforme</li>
              </ul>
            </Section>

            <Section title="6. Sécurité et protection des données">
              <p>TrezoOne met en œuvre des mesures techniques et organisationnelles destinées à assurer la sécurité des données.</p>
              <p className="mt-3">Les données sont hébergées au sein de l'Union européenne sur une infrastructure exploitée par Amazon Web Services.</p>
              <p className="mt-3">Le traitement des données personnelles est réalisé conformément au Règlement général sur la protection des données.</p>
              <p className="mt-3">Les modalités de traitement sont détaillées dans la{' '}
                <Link to="/politique-confidentialite" className="text-blue-600 hover:underline">
                  politique de confidentialité
                </Link>{' '}accessible sur le site.
              </p>
            </Section>

            <Section title="7. Disponibilité du service">
              <p>TrezoOne s'efforce d'assurer l'accessibilité du service 24 heures sur 24 et 7 jours sur 7.</p>
              <p className="mt-3">Toutefois, l'éditeur ne peut garantir une disponibilité permanente.</p>
              <p className="mt-3">Des interruptions peuvent intervenir notamment pour :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>maintenance technique</li>
                <li>mises à jour du service</li>
                <li>incidents techniques</li>
                <li>cas de force majeure</li>
              </ul>
            </Section>

            <Section title="8. Propriété intellectuelle">
              <p>L'ensemble des éléments composant la plateforme TrezoOne, notamment :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>le code source</li>
                <li>l'interface utilisateur</li>
                <li>les fonctionnalités</li>
                <li>les bases de données</li>
                <li>les logos et éléments graphiques</li>
              </ul>
              <p className="mt-4">sont protégés par les lois relatives à la propriété intellectuelle.</p>
              <p className="mt-3">Toute reproduction, modification ou exploitation non autorisée est interdite.</p>
            </Section>

            <Section title="9. Suspension ou suppression du compte">
              <p>L'éditeur se réserve le droit de suspendre ou supprimer un compte utilisateur en cas :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>de violation des présentes CGU</li>
                <li>d'utilisation frauduleuse du service</li>
                <li>de tentative d'accès non autorisé au système</li>
                <li>de non-respect de la législation applicable</li>
              </ul>
            </Section>

            <Section title="10. Résiliation">
              <p>L'utilisateur peut supprimer son compte à tout moment.</p>
              <p className="mt-3">La suppression du compte entraîne la fermeture de l'accès au service.</p>
              <p className="mt-3">Les données peuvent être conservées pendant une durée limitée conformément aux obligations légales et à la politique de confidentialité.</p>
            </Section>

            <Section title="11. Absence de garantie sur les analyses et prévisions">
              <p>TrezoOne est un outil d'aide à la gestion financière.</p>
              <p className="mt-3">Les analyses, indicateurs et prévisions générés par la plateforme reposent exclusivement sur les données saisies ou importées par les utilisateurs.</p>
              <p className="mt-3">L'éditeur ne garantit pas :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>l'exactitude des données saisies par les utilisateurs</li>
                <li>la pertinence des prévisions financières générées</li>
                <li>l'absence d'erreurs résultant de données inexactes ou incomplètes</li>
              </ul>
              <p className="mt-4">Les résultats fournis par TrezoOne ont une valeur strictement indicative.</p>
              <p className="mt-3">Ils ne constituent en aucun cas :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>un conseil financier</li>
                <li>un conseil comptable</li>
                <li>un conseil juridique</li>
                <li>une recommandation de gestion</li>
              </ul>
              <p className="mt-4">L'utilisateur demeure seul responsable des décisions prises sur la base des informations fournies par le service.</p>
            </Section>

            <Section title="12. Limitation de responsabilité">
              <p>Dans toute la mesure permise par la législation applicable, la responsabilité de l'éditeur ne pourra être engagée que pour les dommages directs résultant d'une faute prouvée.</p>
              <p className="mt-3">L'éditeur ne pourra en aucun cas être tenu responsable des dommages indirects, notamment :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>pertes financières</li>
                <li>pertes de revenus</li>
                <li>pertes d'exploitation</li>
                <li>pertes de données</li>
                <li>préjudices commerciaux ou d'image</li>
              </ul>
              <p className="mt-4">En tout état de cause, la responsabilité totale de l'éditeur, toutes causes confondues, est limitée au montant total des sommes éventuellement payées par l'utilisateur pour l'utilisation du service TrezoOne au cours des douze (12) derniers mois précédant la survenance du dommage.</p>
              <p className="mt-3">Cette limitation de responsabilité s'applique dans toute la mesure autorisée par la loi.</p>
            </Section>

            <Section title="13. Modification des CGU">
              <p>Les présentes CGU peuvent être modifiées à tout moment afin de refléter l'évolution du service ou des obligations légales.</p>
              <p className="mt-3">Les utilisateurs seront informés en cas de modification substantielle.</p>
              <p className="mt-3">La poursuite de l'utilisation du service vaut acceptation des nouvelles conditions.</p>
            </Section>

            <Section title="14. Droit applicable">
              <p>Les présentes CGU sont régies par le droit français.</p>
              <p className="mt-3">En cas de litige relatif à leur interprétation ou à leur exécution, les juridictions françaises seront compétentes.</p>
            </Section>

          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap gap-1">
      <span className="text-gray-500 min-w-[140px]">{label}</span>
      <span className="text-gray-800 font-medium">{value}</span>
    </div>
  );
}
